import React, { useState, useContext, useEffect } from "react";
import classnames from "classnames";
import { BrandContext } from "./BrandContext.js";
import Item from "./Item";
import { VenueBrandForContext } from "./venueBrand.js";
import type { Section } from "./types.js";

interface SectionProps {
  section: Section;
}

const SectionBlurb = ({ section }: SectionProps) => {
  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useContext(BrandContext) as VenueBrandForContext;
  if (!section.sectionBlurb) {
    return null;
  }
  return (
    <div
      className={classnames("text-xs italic", {
        "font-light text-center": isElegant,
      })}
    >
      {section.sectionBlurb}
    </div>
  );
};

const SectionFooter = ({ section }: SectionProps) => {
  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useContext(BrandContext) as VenueBrandForContext;
  if (!section.sectionBlurb) {
    return null;
  }
  return (
    <div
      className={classnames("text-xs italic", {
        "font-light text-center": isElegant,
        "px-12": isBrutalist || isPlayful,
      })}
    >
      {section.footer}
    </div>
  );
};

const SectionHeader = ({ section }: SectionProps) => {
  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useContext(BrandContext) as VenueBrandForContext;
  return (
    <div
      className={classnames("flex-1", {
        "uppercase font-light text-center": isElegant,
        "uppercase font-bold": isBrutalist,
        "border-b-2 mb-4  border-venue-brand font-medium text-lg": isSimple,
        "font-semibold text-xl text-venue-brand": isPlayful,
      })}
    >
      {section.name}
    </div>
  );
};
const SectionDefaults = {
  Blurb: SectionBlurb,
  Footer: SectionFooter,
  Header: SectionHeader,
};
type SectionComponent = React.FC<SectionProps>;
const Section = ({
  section,
  SectionBlurb = SectionDefaults.Blurb,
  SectionFooter = SectionDefaults.Footer,
  SectionHeader = SectionDefaults.Header,
}: {
  section: Section;
  SectionBlurb?: SectionComponent;
  SectionFooter?: SectionComponent;
  SectionHeader?: SectionComponent;
}) => {
  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useContext(BrandContext) as VenueBrandForContext;

  return (
    <div
      className={classnames("space-y-8", {
        "font-light mb-32 pb-12 ": isElegant,
      })}
    >
      <div
        className={classnames("bg-white space-y-8 ", {
          "shadow-brutalist mx-4 border-2 border-venue-brand p-24 mb-12":
            isBrutalist,
          "rounded border-neutral-500 ": isSimple,
          "shadow rounded-lg p-16": isPlayful,
          "": isElegant,
        })}
        style={{ borderColor: isPlayful ? venueBrand.brandColor : undefined }}
      >
        <div className={classnames({ "pb-16": isPlayful || isBrutalist })}>
          <SectionHeader section={section} />
          <SectionBlurb section={section} />
        </div>
        <table
          className={classnames("w-full unstyled", {
            "space-y-4": isPlayful,
            "space-y-8": !isPlayful,
          })}
        >
          <tbody>
            {section.items.map((item, index) => (
              <React.Fragment key={`${item.uuid}-${index}`}>
                <Item
                  item={item}
                  pricingType={section.pricingType || "default"}
                  itemDescription={section.itemDescription}
                />
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <SectionFooter section={section} />
    </div>
  );
};

export default Section;
